import {Directive, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormControl} from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {catchError, concatMap, debounceTime, distinctUntilChanged, map, retry, scan, switchMap, takeWhile, tap} from 'rxjs/operators';
import {PageResponse} from '../../models/page-response';
import {Identifiable} from '../../models/identifiable';
import {filteredInfiniteScrollObservable} from '../../utils/pagination';

type Filter = { [key: string]: any };

export enum FetchMode {
    Paginated,
    PartialList,
    Full
}

@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class ItemSelectComponent<T, F extends Filter> implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() multiple = false;
    @Input() placeholder = '';
    @Input() readonly = false;
    @Input() addTag: boolean | ((term: string) => T | Promise<T>) = false;
    @Input() addTagText = 'Voeg toe: ';
    @Input() addTagValid = true;
    @Input() inputAttrs: { [key: string]: string } | undefined;

    @Output() search = new EventEmitter<{ term: string }>();

    @HostBinding('class') classes = 'app-select';

    formControl: FormControl = new FormControl(null);
    disabled = false;
    loading = false;
    onInput = new BehaviorSubject<string>('');
    loadMoreSubject = new BehaviorSubject<null>(null);
    items = this.itemsObservable(this.fetchMode);
    selectItems: T[] = [];
    bindLabel = 'name';
    bindValue: string | undefined = undefined;

    private onChange!: (value: T) => void;
    private onTouched!: () => void;

    private formSubscription!: Subscription;

    scrollToEnd = () => this.loadMoreSubject.next(null);

    // This is a legitimate configuration value, not a DI token, so ignoring lint rule.
    // eslint-disable-next-line @angular-eslint/prefer-inject
    constructor(private fetchMode: FetchMode = FetchMode.Paginated) {
    }

    refresh() {
        this.items = this.itemsObservable(this.fetchMode);
    }

    loadMorePages(page: number, filter: F & { term: string }): Observable<PageResponse<T>> {
        throw Error('Not implemented');
    }

    loadMoreItems(filter: F & { term: string }): Observable<T[]> {
        throw Error('Not implemented');
    }

    abstract getLabelText(item: T): string;

    getFilter(): Observable<F> {
        return of({} as F);
    }

    compare(a: T, b: T): boolean {
        return a === b;
    }

    getSubText(item: T): string | null {
        return null;
    }

    registerOnChange(fn: (value: T) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.formControl.disable();
        } else {
            this.formControl.enable();
        }
    }

    writeValue(value: string): void {
        this.formControl.patchValue(value);
    }

    ngOnInit(): void {
        this.formSubscription = this.formControl.valueChanges.subscribe((value) => {
            if (this.onChange) {
                this.onChange(value);
            }
            if (this.onTouched) {
                this.onTouched();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
    }

    private itemsObservable(fetchMode: FetchMode): Observable<T[]> {
        const searchAndFilterObservable = combineLatest([this.getFilter(), this.onInput.pipe(debounceTime(200), distinctUntilChanged())])
            .pipe(map(([filter, term]) => ({...filter, term})));

        if (fetchMode !== FetchMode.Full) {
            return filteredInfiniteScrollObservable(
                searchAndFilterObservable,
                this.loadMoreSubject,
                this.wrapLoading((page, filter) => {
                    return fetchMode === FetchMode.Paginated ? this.loadMorePages(page, filter) : this.loadMoreItems(filter);
                })
            );
        } else {
            return searchAndFilterObservable.pipe(
                switchMap((filter) => {
                    return this.loadMoreItems(filter);
                }),
                catchError((err) => {
                    // Catch error to prevent observable finishing / error-ing
                    console.error('Failed retrieving data:', err);
                    return of([]);
                })
            );
        }
    }

    private wrapLoading<F>(loadingFunc: (page: number, filter: F & {term: string}) => Observable<any>) {
        this.loading = true;

        return (page: number, filter: F & {term: string}) => loadingFunc(page, filter).pipe(
            tap(() => this.loading = false)
        );
    }
}

export abstract class EntitySelectComponent<E extends Identifiable, F extends Filter> extends ItemSelectComponent<E, F> {
    compare(a: E, b: E): boolean {
        if (!a || !b) {
            return false;
        }

        return a.id === b.id;
    }
}
